import { Toast } from '@modusign/design-system';
import { findFieldNode, useFields } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { FieldNode } from '@modusign/react-signing/types';
import { useSelector } from 'react-redux';

import {
  FIELD_MAX_COUNT,
  MAX_DROPDOWN_FIELD_COUNT,
  MAX_FIELD_COUNT_PER_AUTOFILL_GROUP,
} from '@app/constants/field';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { getDefaultSignatureSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getDefaultSignatureSize';
import { ReactSigningConfigurationFieldRenderer } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers';
import { countFields } from '@app/features/apply-react-signing/libs/countFields';
import { filterFieldNodes } from '@app/features/apply-react-signing/libs/filterFieldNode';
import { isAutofillableField } from '@app/features/apply-react-signing/libs/isAutofillableField';
import { isSignerResizableSignatureField } from '@app/features/apply-react-signing/libs/isSignerResizableSignatureField';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import getIsDataLabelEditable from '@app/store/selectors/getIsDataLabelEditable';

import './PDFSetupBusinessPage.scss';

interface PDFSetupBusinessPageProps {
  pageNumber: number;
  scale: number;
  viewport: {
    height: number;
    width: number;
  };
}
function PDFSetupBusinessPage({ pageNumber, scale, viewport }: PDFSetupBusinessPageProps) {
  const { addField, fields } = useFields();
  const { ui, resetInsertingField } = useUIState();
  const isDataLabelEditable = useSelector(getIsDataLabelEditable);
  const absoluteViewport = createAbsoluteSize(viewport);
  const isInMobileBreakpoint = useResponsiveValue([true, false]);

  function handlePageClick(e: React.MouseEvent<HTMLDivElement>) {
    const { nativeEvent } = e;
    const currentFieldCount = countFields(fields);

    const { insertingField } = ui;

    const ADDING_FIELD_COUNT = 1;
    if (currentFieldCount + ADDING_FIELD_COUNT > FIELD_MAX_COUNT) {
      Toast.warning({
        title: '입력란 추가 실패',
        description: `한 문서에 최대 ${FIELD_MAX_COUNT.toLocaleString(
          'ko-KR'
        )}개까지의 입력란을 추가할 수 있습니다. (현재 ${currentFieldCount.toLocaleString(
          'ko-KR'
        )}개)`,
      });
      resetInsertingField();
      return;
    }

    const dropdownFieldNodesPerTypeCount = filterFieldNodes(
      fields,
      (field) => field.type === 'DROPDOWN'
    ).length;

    if (
      insertingField &&
      dropdownFieldNodesPerTypeCount + ADDING_FIELD_COUNT > MAX_DROPDOWN_FIELD_COUNT
    ) {
      Toast.warning({
        title: '입력란 추가 실패',
        description: `한 문서에 최대 ${MAX_DROPDOWN_FIELD_COUNT.toLocaleString(
          'ko-KR'
        )}개까지의 드롭다운 입력란을 추가할 수 있습니다. (현재 ${dropdownFieldNodesPerTypeCount.toLocaleString(
          'ko-KR'
        )}개)`,
      });
      resetInsertingField();
      return;
    }

    const autofillFieldNodesPerType = filterFieldNodes(fields, (field) => {
      return (
        isAutofillableField(field) &&
        !!field.parent &&
        field.type === insertingField?.field.type &&
        insertingField?.field.participantReference.id === field.participantReference.id
      );
    });

    const autofillableFieldCount = autofillFieldNodesPerType.length;

    if (
      insertingField &&
      isAutofillableField(insertingField.field) &&
      autofillableFieldCount + ADDING_FIELD_COUNT > MAX_FIELD_COUNT_PER_AUTOFILL_GROUP
    ) {
      Toast.warning({
        title: '입력란 추가 실패',
        description: `각 자동 입력란은 최대 ${MAX_FIELD_COUNT_PER_AUTOFILL_GROUP.toLocaleString(
          'ko-KR'
        )}개씩만 추가할 수 있습니다.`,
      });
      resetInsertingField();
      return;
    }

    if (!insertingField) {
      return;
    }

    let autofillFieldGroupId: string | null = null;
    if (isAutofillableField(insertingField.field)) {
      let autofillFieldGroup = findFieldNode(fields as FieldNode[], (field) => {
        if (field.participantReference.id === insertingField.participantId) {
          if (field.type === 'AUTOFILL_GROUP') {
            return field.children.some((child) => child.type === insertingField.field.type);
          }
        }
        return false;
      });

      if (!autofillFieldGroup) {
        autofillFieldGroup = addField({
          type: 'AUTOFILL_GROUP',
          participantId: insertingField.participantId,
          position: {
            x: nativeEvent.offsetX,
            y: nativeEvent.offsetY,
            page: pageNumber,
            z: 0,
          },
          viewport: absoluteViewport,
          pdfScale: scale,
        }) as FieldNode;
      }

      autofillFieldGroupId = autofillFieldGroup.id;
    }

    let offset = { x: 0, y: 0 };
    if (
      insertingField.field.type === 'SIGNATURE' &&
      isSignerResizableSignatureField(insertingField.field)
    ) {
      const defaultSize = getDefaultSignatureSize(insertingField.field.config.allow, scale);
      offset = {
        x: defaultSize.width / 2,
        y: defaultSize.height / 2,
      };
    }

    addField({
      ...insertingField.field,
      participantId: insertingField.participantId,
      position: {
        x: nativeEvent.offsetX + offset.x,
        y: nativeEvent.offsetY + offset.y,
        page: pageNumber,
        z: 0,
      },
      viewport: absoluteViewport,
      pdfScale: scale,
      groupId: autofillFieldGroupId,
    });

    resetInsertingField();
  }

  return (
    <div className="PDFSetupBusinessPage">
      {ui.insertingField && (
        <div // eslint-disable-line
          className="PDFSetupBusinessPage__virtual-layer"
          onClick={handlePageClick}
        />
      )}
      <ReactSigningConfigurationFieldRenderer
        initialContextMenuOpened
        isDataLabelEditable={isDataLabelEditable}
        isPreview={false}
        page={pageNumber}
        restrictModification={isInMobileBreakpoint}
        scale={scale}
        viewport={absoluteViewport}
      />
    </div>
  );
}

export default PDFSetupBusinessPage;
